/**
 * Workspace settings — admins can rename, invite, promote, demote.
 * Everyone can read the member list and see the email domain.
 *
 * "Workspace" is what we call a tenant in the UI — friendlier than
 * the DB-level "tenant". The route is still /tenant and the API
 * lives at /api/me/tenant for consistency with the schema.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { Avatar } from '../components/Avatar';
import { LoadingBlock } from '../components/Spinner';
import { Link } from '../router';
import type { User } from '../../shared/types';

interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  emailDomain: string;
  isPersonal: boolean;
  createdAt: number;
}

interface TenantMember {
  id: string;
  email: string;
  name: string;
  avatarHue: number;
  avatarUrl: string | null;
  tenantRole: 'admin' | 'member';
  lastSeenAt: number;
  createdAt: number;
}

interface PendingInvite {
  email: string;
  invitedBy: string;
  createdAt: number;
}

export function TenantScreen({ user }: { user: User }) {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [members, setMembers] = useState<TenantMember[] | null>(null);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [boardCount, setBoardCount] = useState<number>(0);
  const [myRole, setMyRole] = useState<'admin' | 'member'>('member');
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteHint, setInviteHint] = useState<string | null>(null);

  async function refresh() {
    try {
      const r = await api.myTenant();
      setTenant(r.tenant);
      setMembers(r.members);
      setPending(r.pendingInvites);
      setBoardCount(r.boardCount);
      setMyRole(r.myRole);
      setName(r.tenant.name);
    } catch (e) {
      setNameError(e instanceof Error ? e.message : 'Failed');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const isAdmin = myRole === 'admin';
  const nameDirty = tenant !== null && name.trim() !== tenant.name;

  async function saveName() {
    if (!nameDirty || savingName) return;
    setSavingName(true);
    setNameError(null);
    try {
      await api.patchTenant({ name: name.trim() });
      setTenant((t) => (t ? { ...t, name: name.trim() } : t));
      setSavedHint(true);
      window.setTimeout(() => setSavedHint(false), 1500);
    } catch (e) {
      setNameError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSavingName(false);
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.includes('@') || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    setInviteHint(null);
    try {
      const r = await api.inviteToTenant(inviteEmail.trim().toLowerCase());
      setInviteEmail('');
      setInviteHint(
        r.already ? 'They already have an account here.' : 'Invite sent — they\'ll see it on first sign-in.',
      );
      window.setTimeout(() => setInviteHint(null), 2500);
      await refresh();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setInviteBusy(false);
    }
  }

  async function cancelInvite(email: string) {
    if (!confirm(`Cancel the invite to ${email}?`)) return;
    try {
      await api.cancelTenantInvite(email);
      setPending((cur) => cur.filter((p) => p.email !== email));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    }
  }

  async function toggleRole(m: TenantMember) {
    const next: 'admin' | 'member' = m.tenantRole === 'admin' ? 'member' : 'admin';
    const verb = next === 'admin' ? 'Make this person an admin?' : 'Remove admin from this person?';
    if (!confirm(verb)) return;
    try {
      await api.setTenantMemberRole(m.id, next);
      setMembers((cur) =>
        (cur ?? []).map((x) => (x.id === m.id ? { ...x, tenantRole: next } : x)),
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    }
  }

  if (!tenant) {
    return <LoadingBlock label="Loading workspace…" />;
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight">
            Workspace
            {tenant.isPersonal && (
              <span className="ml-2 align-middle rounded-full bg-ink-3 px-2 py-0.5 text-[11px] font-normal text-cream-3">
                Personal
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-cream-3">
            {tenant.name} · {members?.length ?? 0} member{members?.length === 1 ? '' : 's'}
            {pending.length > 0 && ` · ${pending.length} pending`} · {boardCount} board
            {boardCount === 1 ? '' : 's'}
          </p>
        </div>
        <Link to="/" className="btn btn-ghost text-xs">
          Back home
        </Link>
      </header>

      <section className="panel flex flex-col gap-3 p-5">
        <h2 className="text-sm font-medium">About this workspace</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
          <label className="text-xs text-cream-3">Name</label>
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <input
                className="input flex-1 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
              <button
                type="button"
                className="btn btn-primary text-xs"
                disabled={!nameDirty || savingName || !name.trim()}
                onClick={saveName}
              >
                {savingName ? 'Saving…' : 'Save'}
              </button>
              {savedHint && <span className="text-xs text-leaf">Saved ✓</span>}
            </div>
          ) : (
            <span className="text-sm">{tenant.name}</span>
          )}

          {/* Slug + email_domain are stable identifiers. For personal
              workspaces and user-created ones they're synthetic
              (`personal-…` / `ws-…`) and just leak implementation
              detail — hide unless they're a real human-meaningful
              value (i.e. the workspace was auto-created from a real
              email domain on first sign-in). */}
          {!isSyntheticSlug(tenant.slug) && (
            <>
              <label className="text-xs text-cream-3">Slug</label>
              <span className="font-mono text-sm">{tenant.slug}</span>
            </>
          )}

          {!isSyntheticEmailDomain(tenant.emailDomain) ? (
            <>
              <label className="text-xs text-cream-3">Email domain</label>
              <span className="font-mono text-sm">@{tenant.emailDomain}</span>
            </>
          ) : tenant.isPersonal ? null : (
            <>
              <label className="text-xs text-cream-3">Email domain</label>
              <span className="text-sm text-cream-3">
                None set — anyone you invite by email joins individually.
              </span>
            </>
          )}

          <label className="text-xs text-cream-3">Your role</label>
          <span className="text-sm">{isAdmin ? 'Admin' : 'Member'}</span>
        </div>
        {nameError && <p className="text-xs text-rust">{nameError}</p>}
      </section>

      <section className="panel flex flex-col gap-2 p-5">
        <h2 className="text-sm font-medium">Members</h2>
        <p className="text-xs text-cream-3">
          Everyone with a workspace account here, plus people you've invited who haven't
          signed in yet.
        </p>
        {!members ? (
          <LoadingBlock label="Loading members…" size={20} />
        ) : (
          <ul className="mt-2 flex flex-col gap-1.5">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex items-center gap-2.5 rounded-lg bg-ink-3 p-2.5"
              >
                <Avatar user={m} size={28} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="truncate font-medium">
                      {m.name}
                      {m.id === user.id && (
                        <span className="ml-1.5 text-[11px] text-cream-3">(you)</span>
                      )}
                    </span>
                    {m.tenantRole === 'admin' && <span className="chip">Admin</span>}
                  </div>
                  <div className="truncate text-[11px] text-cream-3">{m.email}</div>
                </div>
                <span className="hidden text-[11px] text-cream-3 sm:inline">
                  {relativeSeen(m.lastSeenAt)}
                </span>
                {isAdmin && m.id !== user.id && (
                  <button
                    type="button"
                    className="btn btn-ghost !px-2 !py-1 text-[11px]"
                    onClick={() => toggleRole(m)}
                  >
                    {m.tenantRole === 'admin' ? 'Demote to member' : 'Make admin'}
                  </button>
                )}
              </li>
            ))}
            {pending.map((p) => (
              <li
                key={p.email}
                className="flex items-center gap-2.5 rounded-lg bg-ink-3 p-2.5"
              >
                <span className="inline-block h-7 w-7 flex-shrink-0 rounded-full border border-dashed border-cream-4" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="truncate font-medium text-cream-2">{p.email}</span>
                    <span className="chip">Pending</span>
                  </div>
                  <div className="text-[11px] text-cream-3">
                    Will join on first sign-in
                  </div>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btn-ghost !px-2 !py-1 text-[11px] text-rust"
                    onClick={() => cancelInvite(p.email)}
                  >
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {isAdmin && (
          <form
            className="mt-3 flex flex-col gap-2 rounded-xl border border-ink-rim bg-ink-3 p-3 sm:flex-row"
            onSubmit={invite}
          >
            <input
              className="input flex-1"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Invite to the workspace — name@anywhere.com"
            />
            <button
              type="submit"
              className="btn btn-primary inline-flex items-center justify-center whitespace-nowrap"
              disabled={inviteBusy || !inviteEmail.includes('@')}
            >
              {inviteBusy ? 'Inviting…' : 'Send invite'}
            </button>
          </form>
        )}
        {inviteHint && <p className="text-xs text-leaf">{inviteHint}</p>}
        {inviteError && <p className="text-xs text-rust">{inviteError}</p>}
        <p className="text-[11px] text-cream-3">
          Cross-workspace board membership: you don't need to be a member of someone else's
          workspace to be added to their board. Owners can invite anyone.
        </p>
      </section>
    </div>
  );
}

// Synthetic identifiers we don't surface to humans:
// - personal-{userId}      auto-created for every user on first sign-in
// - ws-{tenantId}.local    placeholder for workspaces the user created
//   via the New Workspace dialog (no real email domain attached)
function isSyntheticSlug(slug: string): boolean {
  return /^personal-[a-z0-9]+$/i.test(slug) || /^ws-[a-z0-9]+$/i.test(slug);
}
function isSyntheticEmailDomain(d: string): boolean {
  return (
    /^personal-[a-z0-9]+\.local$/i.test(d) || /^ws-[a-z0-9]+\.local$/i.test(d)
  );
}

function relativeSeen(unixSec: number): string {
  const d = Math.floor(Date.now() / 1000) - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `seen ${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `seen ${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `seen ${Math.floor(d / 86400)}d ago`;
  return `seen ${new Date(unixSec * 1000).toLocaleDateString()}`;
}
